import type { TextFieldProps } from '@components/moleculs/text-field/types';
import TextField from '@mui/material/TextField';
import styled from 'styled-components';

type InputProps = {
    $inputSize: TextFieldProps['inputSize'];
    $isMultiline: boolean;
    $helperTextStatic?: boolean;
};

export const Input = styled(TextField)<InputProps>`
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 0;

    .MuiInputAdornment-root {
        ${(props) => {
            switch (props.$inputSize) {
                case 'xl':
                    return props.theme.typography.bigTextRegular;
                case 'm':
                    return props.theme.typography.textBold;
                default:
                    return props.theme.typography.smallTextBold;
            }
        }};
    }

    .MuiInputBase-root {
        background-color: ${(props) => props.theme.palette.primaryA7};
        border-color: ${(props) => props.theme.palette.primaryA4};
        color: ${(props) => props.theme.palette.textMain};
        letter-spacing: 0.002em;
        border-radius: 8px;
        min-height: 52px;
        transition: all 0.3s;

        ${(props) => {
            switch (props.$inputSize) {
                case 'xl':
                    return props.theme.typography.bigTextRegular;
                case 'm':
                    return props.theme.typography.textBold;
                default:
                    return props.theme.typography.smallTextBold;
            }
        }};

        ${(props) => {
            switch (props.$inputSize) {
                // border 1px and box-sizing: border-box, decreese padding by 1px
                case 's':
                    if (props.$isMultiline) {
                        return {
                            padding: '8px 12px',
                        };
                    }

                    return {
                        padding: '8px 12px',
                        minHeight: 32,
                        maxHeight: 32,
                    };
                case 's/m':
                    if (props.$isMultiline) {
                        return {
                            padding: '12px',
                        };
                    }

                    return {
                        padding: '12',
                        minHeight: 40,
                        maxHeight: 40,
                    };

                case 'xl':
                    if (props.$isMultiline) {
                        return {
                            padding: '13px 12px',
                        };
                    }

                    return {
                        padding: '13px 12px',
                        minHeight: 60,
                        maxHeight: 60,
                    };
                case 'm':
                default:
                    if (props.$isMultiline) {
                        return {
                            padding: '10px 12px',
                        };
                    }

                    return {
                        padding: '10px 12px',
                        minHeight: 44,
                        maxHeight: 44,
                    };
            }
        }};

        input {
            padding: 0;
            ${(props) => {
                switch (props.$inputSize) {
                    case 'xl':
                        return props.theme.typography.bigTextRegular;
                    case 'm':
                        return props.theme.typography.textBold;
                    default:
                        return props.theme.typography.smallTextBold;
                }
            }};
        }

        /* MUI 9 injects '.MuiAutocomplete-root .MuiOutlinedInput-root .MuiAutocomplete-input
           { padding: 7.5px 4px 7.5px 5px }' on the inner Autocomplete input (specificity 0,3,0),
           which defeats the 'input { padding: 0 }' above (a tag selector). Re-apply padding and
           per-size typography with a higher-specificity selector (0,4,0) so the height stays
           driven by 'inputSize'. In MUI 7 this lived on '.MuiInputBase-inputAdornedEnd', but v9
           dropped the adorned/size modifier classes from the <input> element (kept only on root). */
        .MuiInputBase-input.MuiAutocomplete-input {
            padding: 0;
            ${(props) => {
                switch (props.$inputSize) {
                    case 'xl':
                        return props.theme.typography.bigTextRegular;
                    case 'm':
                        return props.theme.typography.textBold;
                    default:
                        return props.theme.typography.smallTextBold;
                }
            }};
        }

        input:-webkit-autofill,
        input:-webkit-autofill:hover,
        input:-webkit-autofill:focus,
        input:-webkit-autofill:active {
            -webkit-box-shadow: 0 0 0 30px transparent inset !important;
        }

        .MuiInputBase-input {
            height: 16px;

            &.Mui-disabled {
                cursor: default;
                color: ${(props) => props.theme.palette.primaryA3};
                -webkit-text-fill-color: unset;
            }
        }

        .MuiBox-root {
            //in case when svg inside fox Mui box
            svg {
                color: unset;
            }
        }

        svg {
            color: ${(props) => props.theme.palette.textLight};
        }

        .Mui-disabled {
            svg {
                color: ${(props) => props.theme.palette.primaryA3};
            }
        }

        &:not(.Mui-disabled):not(.Mui-error):hover {
            border: 1px solid ${(props) => props.theme.palette.primaryA2};
        }

        &.Mui-focused {
            border: 1px solid ${(props) => props.theme.palette.primaryA1};
        }

        input::placeholder {
            font-family: 'Funnel Display', Roboto, sans-serif;
            color: ${(props) => props.theme.palette.textLight};
            opacity: 1;
        }
    }

    .MuiInputAdornment-positionEnd {
        margin-right: 5.5px;
    }

    .MuiFormHelperText-root {
        ${(props) => {
            if (!props.$helperTextStatic) {
                return {
                    position: 'absolute',
                    left: 0,
                    width: 'max-content',
                    bottom: '-20px',
                };
            }

            return {
                alignSelf: 'start',
            };
        }}
        ${(props) => props.theme.typography.smallTextBold}
        color: ${(props) => props.theme.palette.secondaryA4};
    }

    .MuiFormLabel-root.Mui-error {
        color: ${(props) => props.theme.palette.secondaryA4};
    }

    &.Mui-error-container {
        .MuiInputBase-root {
            border-color: ${(props) => props.theme.palette.secondaryA4};
            svg {
                color: ${(props) => props.theme.palette.secondaryA4};
            }
        }
    }

    .helper-container {
        display: flex;
        flex-direction: column;
        gap: 2px;

        .hint {
            ${(props) => props.theme.typography.smallTextBold}
            color: ${(props) => props.theme.palette.textLight};
        }
    }
`;
